import { Component, ElementRef, Input, AfterViewInit, OnDestroy, ViewChild, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import cytoscape, { Core, ElementDefinition } from 'cytoscape';
import dagre from 'cytoscape-dagre';

cytoscape.use(dagre);

@Component({
  selector: 'app-tree',
  standalone: true,
  imports: [CommonModule],
  template: `<div #treeContainer class="tree-container"></div>`,
  styles: [`
    :host {
      display: block;
      height: 100%;
      min-height: 0;
    }

    .tree-container {
      width: 100%;
      height: 100%;
      min-height: 0;
      border-radius: 12px;
      background: #ffffff;
    }
  `]
})
export class TreeComponent implements AfterViewInit, OnDestroy, OnChanges {
  @ViewChild('treeContainer') treeContainer!: ElementRef<HTMLDivElement>;
  @Input() elements: ElementDefinition[] = [];
  @Input() direction: 'TB' | 'LR' = 'TB'; // top-bottom or left-right

  private cy: Core | null = null;
  private resizeObserver: ResizeObserver | null = null;
  private resizeTimer: any = null;

  ngAfterViewInit(): void {
    this.initTree();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['elements'] && !changes['elements'].firstChange && this.cy) {
      this.updateTree();
    }
  }

  ngOnDestroy(): void {
    if (this.resizeTimer) clearTimeout(this.resizeTimer);
    if (this.resizeObserver) this.resizeObserver.disconnect();
    if (this.cy) this.cy.destroy();
  }

  private initTree(): void {
    const container = this.treeContainer.nativeElement;

    this.cy = cytoscape({
      container,
      elements: this.elements,
      style: [
        {
          selector: 'node',
          style: this.getNodeStyle()
        },
        {
          selector: 'node[nodeType = "root"]',
          style: {
            'background-color': '#774AF3',
            'color': '#ffffff',
            'border-color': '#3D6BE0',
            'border-width': 2
          } as any
        },
        {
          selector: 'node[nodeType = "leaf"]',
          style: {
            'border-color': '#98A2B3',
            'border-width': 1.5,
            'border-style': 'dashed'
          } as any
        },
        {
          selector: 'node:selected',
          style: {
            'background-color': '#F5F8FF',
            'border-color': '#774AF3',
            'border-width': 2,
            'shadow-blur': 10,
            'shadow-color': 'rgba(119, 74, 243, 0.2)',
            'shadow-opacity': 1
          } as any
        },
        {
          selector: 'edge',
          style: this.getEdgeStyle()
        },
        {
          selector: 'edge:selected',
          style: {
            'line-color': '#774AF3',
            'target-arrow-color': '#774AF3',
            'width': 3
          } as any
        }
      ],
      layout: this.getLayoutOptions(),
      wheelSensitivity: 0.3,
      minZoom: 0.3,
      maxZoom: 3,
      boxSelectionEnabled: true,
      selectionType: 'single'
    });

    this.cy.on('select', 'node', (event) => {
      this.highlightConnectedEdges(event.target);
    });

    this.cy.on('unselect', 'node', () => {
      this.resetEdgeStyles();
    });

    this.resizeObserver = new ResizeObserver(() => {
      if (this.resizeTimer) clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(() => {
        if (this.cy) this.cy.resize();
      }, 200);
    });
    this.resizeObserver.observe(container);
  }

  private getLayoutOptions(): any {
    return {
      name: 'dagre',
      rankDir: this.direction, // 'TB' = top-to-bottom, 'LR' = left-to-right
      animate: true,
      animationDuration: 500,
      fit: true,
      padding: 40,
      nodeSep: 50,
      rankSep: 80,
      edgeSep: 20
    };
  }

  private getNodeStyle(): any {
    return {
      'label': 'data(label)',
      'background-color': '#ffffff',
      'color': '#1a1a1a',
      'font-family': 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
      'font-size': '12px',
      'font-weight': 500,
      'text-valign': 'center',
      'text-halign': 'center',
      'padding': '10px',
      'border-width': 1.5,
      'border-color': '#D0D5DD',
      'border-opacity': 1,
      'border-style': 'solid',
      'shape': 'round-rectangle',
      'shadow-blur': 6,
      'shadow-color': 'rgba(0,0,0,0.08)',
      'shadow-opacity': 1,
      'shadow-offset-x': 0,
      'shadow-offset-y': 2,
      'transition-property': 'border-color, shadow-blur, shadow-opacity',
      'transition-duration': 200,
      'width': (ele: any) => {
        const label = ele.data('label') || '';
        return Math.max(80, label.length * 7.5 + 32);
      },
      'height': 36
    };
  }

  private getEdgeStyle(): any {
    return {
      'width': 1.5,
      'line-color': '#D0D5DD',
      'target-arrow-color': '#98A2B3',
      'target-arrow-shape': 'triangle',
      'arrow-scale': 1,
      'curve-style': 'taxi', // clean right-angle tree connectors
      'taxi-direction': this.direction === 'TB' ? 'vertical' : 'horizontal',
      'label': 'data(label)',
      'font-size': '10px',
      'color': '#667085',
      'font-family': 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
      'font-weight': 500,
      'text-background-color': '#ffffff',
      'text-background-opacity': 1,
      'text-background-padding': 3,
      'text-rotation': 'autorotate'
    };
  }

  private updateTree(): void {
    if (!this.cy) return;
    this.cy.elements().remove();
    this.cy.add(this.elements);
    const layout = this.cy.layout(this.getLayoutOptions());
    layout.run();
  }

  private highlightConnectedEdges(node: any): void {
    this.cy?.edges().style({ 'opacity': 0.2, 'width': 1 } as any);
    node.connectedEdges().style({
      'opacity': 1,
      'width': 3,
      'line-color': '#774AF3',
      'target-arrow-color': '#774AF3'
    } as any);
  }

  private resetEdgeStyles(): void {
    this.cy?.edges().style({
      'opacity': 1,
      'width': 1.5,
      'line-color': '#D0D5DD',
      'target-arrow-color': '#98A2B3'
    } as any);
  }
}